import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import {
  useFormat,
  hi,
  lo,
  useModernization,
  usePayback,
  type ModernPackage,
} from '../components/modernization.js';
import { LinkButton, LoadError, Loading, TableBox } from '../components/ui.js';
import { errorMessage } from '../errors.js';

/**
 * The packages side by side (T-39, site-modernization.html, compare): one table – contents, hours
 * and cost with AI and classic, speed, benefit, payback, risks, for whom and access.
 */
export function ModernizationCompare() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const f = useFormat();
  const payback = usePayback();
  const query = useModernization(siteId);

  if (query.isPending) return <Loading />;
  if (query.isError)
    return (
      <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
    );
  const packages = query.data.packages;

  // Numbers right-aligned in the sheet's monospace (.tbl td.num), words as text.
  const rows: [string, (p: ModernPackage) => string, boolean][] = [
    ['modern.compare.includes', (p) => t(`modern.packages.${p.key}.summary`), false],
    ['modern.compare.hours', (p) => `${f.span(p.hours.ai)} / ${f.hours(p.hours.classic)}`, true],
    [
      'modern.compare.cost',
      (p) => `${f.euroRange(p.cost.ai)} / ${f.euroRange(p.cost.classic)}`,
      true,
    ],
    [
      'modern.compare.speed',
      (p) =>
        p.effect.lcpMs
          ? t('modern.compare.lcp', {
              from: f.num(lo(p.effect.lcpMs) / 1000),
              to: f.num(hi(p.effect.lcpMs) / 1000),
            })
          : t('modern.compare.noSpeed'),
      true,
    ],
    ['modern.compare.gain', (p) => t(`modern.packages.${p.key}.gain`), false],
    ['modern.payback.label', (p) => payback(p, query.data.facts.lcpMs), false],
    ['modern.compare.risks', (p) => t(`modern.packages.${p.key}.risks`), false],
    ['modern.compare.forWhom', (p) => t(`modern.packages.${p.key}.forWhom`), false],
    [
      'modern.compare.access',
      (p) => t(`modern.packages.${p.key}.access`).split('\n')[0] ?? '',
      false,
    ],
  ];

  return (
    <>
      <div className="area-hd">
        <Link className="crumb" to={`/sites/${siteId}/modernization`}>
          <span aria-hidden="true">← </span>
          {t('modern.back')}
        </Link>
        <h1>{t('modern.compare.title')}</h1>
        <p className="sub">{t('modern.compare.intro')}</p>
      </div>
      <TableBox label={t('modern.compare.title')}>
        <table className="compare">
          <thead>
            <tr>
              <td />
              {packages.map((p) => (
                <th key={p.key} scope="col">
                  {t(`modern.packages.${p.key}.title`)}
                  {p.recommended ? (
                    <span className="badge acc nodot">{t('modern.recommended')}</span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, cell, numeric]) => (
              <tr key={label}>
                <th scope="row">{t(label)}</th>
                {packages.map((p) => (
                  <td key={p.key} className={numeric ? 'num' : undefined}>
                    {numeric ? <Halves text={cell(p)} /> : cell(p)}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td />
              {packages.map((p) => (
                <td key={p.key}>
                  <LinkButton
                    to={`/sites/${siteId}/modernization/${p.key}`}
                    variant="secondary"
                    size="sm"
                  >
                    {t('modern.details')}
                  </LinkButton>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </TableBox>
      <p className="spec">{t('modern.disclaimer')}</p>
    </>
  );
}

/**
 * "with AI / classic": each range stays on one line, the pair may break after the slash, so four
 * packages fit the card instead of pushing the table past it (SP-UX12b).
 */
function Halves({ text }: { text: string }) {
  // The separator outside the unbreakable halves: that is where the line may break.
  return text.split(' / ').map((part, i) => (
    <Fragment key={i}>
      {i > 0 ? ' / ' : null}
      <span className="whitespace-nowrap">{part}</span>
    </Fragment>
  ));
}
